import React, { useMemo, useState } from 'react';
import map from 'lodash/map';
import StepWizard from 'react-step-wizard';
import styled from 'styled-components';

import AddEmailAddressStep1 from 'src/presentation/components/pages/email/components/EmailAddressStep1';
import AddEmailAddressStep2 from 'src/presentation/components/pages/email/components/AddEmailAddressStep2';
import EmbedModalWithGreenHeader from 'src/presentation/components/pages/shared/EmbedModalWithGreenHeader';
import { DomainError } from '@ad-scripts/application/lib/error';
import { EmailAddress } from 'src/models/Email/EmailAddress';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import { StepContainer } from 'src/presentation/components/pages/email/components/StepWizardLayout';

type AddEmailAddressModalType = {
  errors: DomainError[];
  handleCloseModal: () => void;
  step: number;
  emailAddress: EmailAddress | null;
};

const STEP_DATA_AID = 'add-email-address-wizard-step-';

export default function AddEmailAddressModal({
  step,
  errors,
  handleCloseModal,
  emailAddress,
}: AddEmailAddressModalType) {
  const [value, setValue] = useState<EmailAddress | null>(emailAddress);

  const steps = useMemo(
    () => ({
      step1: <AddEmailAddressStep1 errors={errors} setValue={setValue} onCancel={handleCloseModal} />,
      step2: <AddEmailAddressStep2 onDone={handleCloseModal} value={value!} isWizardStep />,
    }),
    [errors, setValue, handleCloseModal, value]
  );

  const getFormTitle = (step: number) => `Add email address - step ${step} of ${Object.keys(steps ?? {}).length}`;
  const [modalTitle, setModalTitle] = useState(getFormTitle(step));

  const handleStepChange = (stepChange: { previousStep: number; activeStep: number }) => {
    setModalTitle(getFormTitle(stepChange.activeStep));
  };

  const getModalContent = () => (
    <StyledStepWizard onStepChange={handleStepChange} initialStep={step}>
      {map(Object.values(steps), (step, index) => (
        <StepContainer key={index} data-aid={`${STEP_DATA_AID}${index + 1}`}>
          {step}
        </StepContainer>
      ))}
    </StyledStepWizard>
  );

  return (
    <EmbedModalWithGreenHeader
      data-aid="new-email-address"
      title={modalTitle}
      disableOutsideClick={true}
      modalSize={ModalSize.EXTRALARGE}
      content={getModalContent()}
      onCancel={handleCloseModal}
    />
  );
}

const StyledStepWizard = styled(StepWizard)`
  overflow: hidden;
`;
